import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { Box, Grid, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import cortisol_reference from '../../config/cortisol_reference.json';
import { HormoneSnapshot } from '../../types';
import { dateToString } from '../../utils';
import { stressColors } from '../../views/CortisolReportOverview';
import { RootineMultiSelect } from '../RootineMultiSelect';

enum SampleTimes {
  'waking',
  'mid-day',
  'bedtime',
}

export interface CortisolLineChartProps {
  snapshots: HormoneSnapshot[];
}

type CortisolResult = {
  time: string;
  result: number;
  low: number;
  average: number;
  high: number;
  diff?: number;
};

const MAX_Y_RANGE = 30;

const roundNumber = (num: number): number => {
  return Math.round(num * 100) / 100;
};

const referenceLine = cortisol_reference.data;
const referenceRange = cortisol_reference.reference_range;

export const CortisolLineChart = ({
  snapshots,
}: CortisolLineChartProps): JSX.Element => {
  const [snapshotIdx, setSnapshotIdx] = useState(0);

  const highestResult = snapshots[snapshotIdx].test_results
    .map((r) => r.result)
    .sort((a, b) => a - b)
    .reverse()[0];

  const yRange =
    highestResult <= 15
      ? 15
      : highestResult <= MAX_Y_RANGE
      ? Math.ceil(highestResult)
      : MAX_Y_RANGE;

  const data: CortisolResult[] = snapshots[snapshotIdx].test_results.map(
    (result, resultIdx) => {
      let diff;
      const prevSnapshot = snapshots[snapshotIdx + 1] || null;
      if (prevSnapshot) {
        diff = roundNumber(
          result.result - prevSnapshot.test_results[resultIdx].result
        );
      }
      return {
        time: SampleTimes[resultIdx] || `sample ${resultIdx + 1}`,
        high: roundNumber(result.max_value),
        average: roundNumber((result.min_value + result.max_value) / 2),
        low: roundNumber(result.min_value),
        result: roundNumber(result.result),
        diff,
      };
    }
  );

  const dots: JSX.Element[] = snapshots[snapshotIdx].test_results.map(
    (res, i) => {
      const sampleDate = new Date(res.sample_datetime);
      const firstSampleDate = new Date(
        snapshots[snapshotIdx].test_results[0].sample_datetime
      );
      return (
        <ReferenceDot
          key={i}
          r={5}
          x={roundNumber(
            0.5 +
              (sampleDate.getTime() - firstSampleDate.getTime()) /
                (60 * 60 * 1000)
          )}
          y={res.result <= yRange ? res.result : yRange}
          fill="black"
          label={{
            value: `${roundNumber(res.result)}`,
            position: res.result <= yRange - 3 ? 'top' : 'bottom',
          }}
        />
      );
    }
  );

  const referenceData = referenceLine.map((point, i) => {
    const range = referenceRange[i];
    return {
      time: point.x,
      refValue: point.y,
      optimal_range: [range.min, range.max],
    };
  });

  const resultDetail = (result: CortisolResult): JSX.Element => {
    let color = stressColors.normal;
    if (result.result > result.high || result.result < result.low) {
      color = stressColors.type_3;
    }
    return (
      <Grid container item xs={12} textAlign="center" rowSpacing={2}>
        <Grid item xs={12}>
          <Typography
            sx={{
              fontSize: 14,
              opacity: 0.5,
              textDecoration: 'underline',
              textUnderlineOffset: 10,
            }}
          >
            {result.time.toLocaleUpperCase()}
          </Typography>
        </Grid>
        <Grid
          container
          item
          xs={12}
          alignItems="center"
          justifyContent="center"
        >
          <Typography sx={{ fontSize: 24, color: color }}>
            {result.result}
          </Typography>
          {result.diff && (
            <Typography pl={1} mt={2} sx={{ fontSize: 16, color: 'info.main' }}>
              {`${result.diff > 0 ? '+' : ''}${result.diff}`}
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  };

  const CustomTooltip = (props: any) => {
    const { active, payload, label } = props;
    if (active && payload && payload.length) {
      if (label === 0) return null;

      return (
        <Box
          alignSelf="right"
          paddingRight={5}
          sx={{ backgroundColor: 'white' }}
        >
          <Stack spacing={0.5} paddingBottom={2}>
            <Typography fontSize={16} sx={{ padding: 2 }}>
              {`Hours Since Awakening: ${roundNumber(label)}`}
            </Typography>
            {payload.map((p: any, i: number) => {
              let value = p.value;
              if (Array.isArray(p.value)) {
                value = `${roundNumber(p.value[0])} - ${roundNumber(
                  p.value[1]
                )}`;
              } else {
                value = roundNumber(value);
              }
              return (
                <Typography
                  key={i}
                  color={p.color}
                  fontSize={16}
                  sx={{ px: 2 }}
                >
                  {`${p.name}: ${value}`}
                </Typography>
              );
            })}
          </Stack>
        </Box>
      );
    }

    return null;
  };

  return (
    <Grid container item xs={12} rowSpacing={2}>
      <Grid container item xs={12} borderBottom={1} borderColor="gray">
        <Grid container item xs={6} alignItems="center" paddingBottom={1}>
          <SignalCellularAltIcon fontSize="small" sx={{ opacity: 0.5 }} />
          <Typography sx={{ fontSize: 14, opacity: 0.5 }}>
            {'CORTISOL RESULTS'}
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign="right" paddingBottom={1}>
          <RootineMultiSelect
            onChange={(select) => {
              setSnapshotIdx(select.key);
            }}
            options={snapshots.map((snap, i) => ({
              key: i,
              value: dateToString(snap.sample_date),
            }))}
            label="TEST DATE"
          />
        </Grid>
      </Grid>
      {data.map((result: CortisolResult, i: number) => (
        <Grid container item key={i} xs={4}>
          {resultDetail(result)}
        </Grid>
      ))}
      <Grid container item xs={12} marginLeft={-4}>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={referenceData}>
            <CartesianGrid />
            <XAxis
              id="xAxis"
              dataKey="time"
              type="number"
              domain={[0, 22]}
              tickCount={12}
              label={{
                value: 'Time Since Awakening (hours)',
                offset: -5,
                position: 'insideBottom',
              }}
            />
            <YAxis
              id="yAxis"
              type="number"
              domain={[0, yRange]}
              tickCount={6}
              label={{
                value: 'Salivary Cortisol (nmol/L)',
                angle: -90,
                offset: 30,
                position: 'insideBottomLeft',
              }}
            />
            <Area
              type="monotone"
              dataKey="optimal_range"
              opacity={0.7}
              stroke={stressColors.normal}
              fill={stressColors.normal}
              name="Optimal Range"
            />
            <Line
              type="monotone"
              dataKey="refValue"
              name="Median"
              strokeWidth={3}
              stroke={'black'}
              dot={false}
            />
            {snapshots.length > 0 && dots.map((d) => d)}
            <Tooltip content={<CustomTooltip />} offset={20} />
          </ComposedChart>
        </ResponsiveContainer>
      </Grid>
    </Grid>
  );
};
